import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Check, Lock } from "lucide-react";

import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Turnstile } from "@/components/forms/Turnstile";
import { QuickQuoteStepLabel } from "@/components/forms/QuickQuoteSteps";
import {
  QQ_HEARD_OPTIONS,
  clearNorwood,
  readNorwood,
  rememberLeadId,
  rememberPhone,
  rememberStep1Norwood,
  trackLeadEventUnlessRepeat,
} from "@/components/forms/quick-quote-shared";
import { startQuickQuote } from "@/lib/forms/quick-quote.functions";
import {
  LEAD_RETRY_DELAYS_MS,
  clearLeadDraft,
  finalLeadError,
  restoreLeadDraft,
  saveLeadDraft,
  sleep,
} from "@/components/forms/lead-retry";
import { rememberW2lPreview } from "@/lib/forms/test-preview";
import { getTrackingParams } from "@/lib/utm-tracking";
import { countBannerStep, withBannerVariant } from "@/lib/banner-ab";
import { norwoodClasses } from "@/lib/site-data";
import { qqTrack, msSinceLoad } from "@/lib/qq-diagnostics";
import { newTurnstileProblemLog, noteTurnstileProblem } from "@/lib/qq-turnstile-report";
import { QQ_STICKY_BAR_CLS, scrollToInvalid } from "@/components/forms/quick-quote-scroll";

const TITLE = "Get a Quick Quote - RESTORE Hair";
const DESCRIPTION =
  "Discover how affordable it is to get your hair back. Get a personalized quick quote on your No-Shave FUE hair restoration procedure from RESTORE Hair.";
const URL = "https://restorehair.com/quick-quote-part-1/";

export const Route = createFileRoute("/quick-quote-part-1")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, follow" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: QuickQuotePart1,
});

/** Boxed field with the label sitting inside the top of the control. */
function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}) {
  // The row id lets a failed Submit scroll the whole box (label, control and
  // message) into view, not only the input inside it.
  return (
    <div id={`${id}-row`}>
      <div
        className={`border bg-background px-4 pb-2 pt-2.5 ${
          error ? "border-destructive ring-1 ring-destructive" : "border-border"
        }`}
      >
        <label
          htmlFor={id}
          className="block text-xs font-medium uppercase tracking-wide text-muted-foreground"
        >
          {label}
        </label>
        {children}
      </div>
      {error && (
        <p id={errorId(id)} className="mt-1 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

// Inline messages for a Submit that fails validation. These are UI text,
// not consent wording: the consent labels below stay as they are.
const FIELD_MESSAGES: Record<string, string> = {
  first_name: "Please enter your first name.",
  last_name: "Please enter your last name.",
  phone: "Please enter your phone number.",
  email: "Please enter your email address.",
  zip: "Please enter your zip code.",
};
const CONSENT_MESSAGE = "Please check this box to continue.";
// A Submit tap on a form with nothing in it gets this one message and the
// first field, not a red error on every control (17 Oct 2026).
const EMPTY_FORM_MESSAGE = "Please fill in your details to get your quote.";
const EMPTY_FORM_ID = "qq1-empty-form";

function errorId(id: string) {
  return `${id}-error`;
}

type Validatable = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function invalidMessage(el: Validatable): string {
  if (el instanceof HTMLInputElement && el.type === "checkbox") return CONSENT_MESSAGE;
  if (el.name === "email" && el.validity.typeMismatch) return "Please enter a valid email address.";
  return FIELD_MESSAGES[el.name] ?? "Please fill out this field.";
}

/**
 * Inline message for a typed field when the visitor leaves it (#69). Uses
 * the same browser validity as the Submit check above, plus a hint for a
 * phone number with too few digits. UI only: no event fires from here.
 */
function blurMessage(el: Validatable): string | null {
  if (!el.validity.valid) return invalidMessage(el);
  if (el.name === "phone") {
    const digits = el.value.replace(/\D/g, "").length;
    if (digits < 10 || digits > 15) return "Please enter a 10-digit phone number, including area code.";
  }
  return null;
}

/** Show a 10-digit US number as (303) 555-1212. The server normalizes any format. */
function formatPhoneForDisplay(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length !== 10) return raw;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

/**
 * Invalid controls in DOM order. The three consent boxes count as invalid
 * when unchecked even if the browser says otherwise: the send below always
 * reports consent as given, so this check is the consent gate.
 */
function findInvalid(form: HTMLFormElement): Validatable[] {
  const out: Validatable[] = [];
  for (const node of Array.from(form.elements)) {
    if (
      !(node instanceof HTMLInputElement) &&
      !(node instanceof HTMLSelectElement) &&
      !(node instanceof HTMLTextAreaElement)
    ) {
      continue;
    }
    const consentUnchecked =
      node instanceof HTMLInputElement && node.name in CONSENT_FIELDS && !node.checked;
    if (consentUnchecked || (node.willValidate && !node.validity.valid)) out.push(node);
  }
  return out;
}

/** True when no field has a value and no box is ticked. */
function formIsEmpty(form: HTMLFormElement): boolean {
  for (const node of Array.from(form.elements)) {
    if (node instanceof HTMLInputElement) {
      if (node.type === "checkbox") {
        if (node.checked) return false;
      } else if (TRACKED_FIELDS.has(node.name) && node.value.trim()) {
        return false;
      }
    } else if (node instanceof HTMLSelectElement && TRACKED_FIELDS.has(node.name) && node.value) {
      return false;
    }
  }
  return true;
}

// The typed contact fields on step 1, top to bottom.
const CONTACT_ORDER = ["first_name", "last_name", "phone", "email", "zip"];

/**
 * Reporting only (19 Oct 2026): what kind of failed Submit this was, so a
 * Submit tapped early is not read as a field the visitor could not fill in.
 * The Submit bar is pinned on phones (#69), so it is often tapped before
 * the visitor reaches the fields below.
 *
 * - kind "empty": nothing filled in and no box ticked.
 * - kind "consent": only consent boxes are unticked.
 * - kind "unfinished": the first contact field that fails and every contact
 *   field below it are blank (Submit tapped before getting to them).
 * - kind "fix": a contact field fails while a field below it is filled in,
 *   or a filled-in field fails its check (an email address, for example).
 *
 * invalid lists the fields that have something typed in and still fail
 * (names only, never values), "none" if there are none.
 */
function errorShape(invalid: Validatable[], empty: boolean): { kind: string; invalid: string } {
  const typedButInvalid = invalid
    .filter((el) => !(el.name in CONSENT_FIELDS) && String(el.value ?? "").trim() !== "")
    .map((el) => el.name || "unknown");
  const failing = new Set(invalid.map((el) => el.name));
  const firstContact = CONTACT_ORDER.findIndex((name) => failing.has(name));
  let kind: string;
  if (empty) kind = "empty";
  else if (firstContact === -1) kind = "consent";
  else if (
    typedButInvalid.length === 0 &&
    CONTACT_ORDER.slice(firstContact).every((name) => failing.has(name))
  ) {
    kind = "unfinished";
  } else kind = "fix";
  return { kind, invalid: typedButInvalid.join(",").slice(0, 100) || "none" };
}

// Field names that funnel events may report. Events carry the name only,
// never the value. Anything outside this list is reported as "other".
const TRACKED_FIELDS = new Set(["first_name", "last_name", "phone", "email", "zip", "heard"]);
const CONSENT_FIELDS: Record<string, string> = {
  consent_contact: "calls",
  consent_terms: "terms",
  consent_sensitive: "sensitive",
};

function trackedFieldName(target: EventTarget | null): string | null {
  const name = (target as { name?: unknown } | null)?.name;
  if (typeof name !== "string" || !name) return null;
  if (TRACKED_FIELDS.has(name)) return name;
  if (name in CONSENT_FIELDS) return `consent_${CONSENT_FIELDS[name]}`;
  return "other";
}

const controlCls =
  "scroll-mb-40 scroll-mt-36 w-full border-0 bg-transparent p-0 pt-1.5 pb-1 text-base text-foreground outline-none focus:ring-0";

/**
 * One consent row (17 Oct 2026). The whole row is the label, so a tap
 * anywhere on it toggles the box; the box is 24 px and the row at least
 * 44 px tall. A tap on a link in the text opens the link and leaves the box
 * as it was (a link is interactive content, so the label does not act).
 * The wording is unchanged.
 */
function Consent({ id, error, children }: { id: string; error?: string; children: ReactNode }) {
  return (
    <div
      id={`${id}-row`}
      className={`-mx-2 rounded-sm px-2 ${
        error
          ? "border border-destructive bg-destructive/5 py-2 ring-1 ring-destructive"
          : "border border-transparent"
      }`}
    >
      {error && (
        <p id={errorId(id)} className="mb-1 text-sm font-medium text-destructive">
          {error}
        </p>
      )}
      <label className="flex min-h-11 cursor-pointer select-none gap-3 py-1.5" htmlFor={id}>
        <input
          id={id}
          name={id}
          type="checkbox"
          required
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId(id) : undefined}
          className={`h-6 w-6 shrink-0 cursor-pointer accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
            error ? "outline outline-2 outline-offset-2 outline-destructive" : ""
          }`}
        />
        <span className="pt-0.5">{children}</span>
      </label>
    </div>
  );
}

// Turnstile timing (8 Oct 2026). A token lives 300 s. Older than
// TOKEN_MAX_AGE_MS counts as expired at Submit; TOKEN_REFRESH_MS is the age
// at which a return to the tab gets a new one.
const TOKEN_WAIT_MS = 1500;
const TOKEN_MAX_AGE_MS = 290_000;
const TOKEN_REFRESH_MS = 240_000;

function QuickQuotePart1() {
  const navigate = useNavigate();
  const start = useServerFn(startQuickQuote);
  // The Turnstile token is a ref, not state (8 Oct 2026). The widget
  // callback sets the token and calls requestSubmit() in the same task,
  // before React renders again, so onSubmit read the old token from state.
  // The resend after a failed send then saw no token and stopped on
  // "Verifying you're human" until the visitor tapped Submit again.
  const tokenRef = useRef<string | null>(null);
  const tokenAtRef = useRef(0);
  const setToken = (t: string | null) => {
    tokenRef.current = t;
    if (t) tokenAtRef.current = Date.now();
  };
  // Turnstile never blocks a lead (8 Oct 2026). With no fresh token at
  // Submit, the page resets the widget and waits at most TOKEN_WAIT_MS, then
  // sends without a token; the server runs its bot checks instead.
  const tokenWaiterRef = useRef<((t: string | null) => void) | null>(null);
  const turnstileIssueRef = useRef<"expired" | "error" | null>(null);
  const hadTokenRef = useRef(false);
  const busyRef = useRef(false);
  const autoResendRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  // The class picker is optional (16 Oct 2026): Submit no longer waits for
  // it. A lead sent without a class is asked for it on step 2, where it is
  // required (quick-quote-page-2.tsx).
  const [norwood, setNorwood] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  // qq1_turnstile_error goes once per code per page (qq-turnstile-report.ts).
  const turnstileProblemsRef = useRef(newTurnstileProblemLog());
  const reportTurnstileProblem = (code: string) => {
    const props = noteTurnstileProblem(turnstileProblemsRef.current, code);
    if (props) qqTrack("qq1_turnstile_error", { ...props, ms: msSinceLoad() });
  };
  // Server failures since the last send. A failed send spends the token, so
  // the first 2 failures get a fresh token and an automatic resend.
  const serverRetriesRef = useRef(0);
  const turnstileResetRef = useRef<(() => void) | null>(null);
  // True while Turnstile shows a challenge that waits for a tap (audit F3).
  const challengeOpenRef = useRef(false);
  const turnstileBoxRef = useRef<HTMLDivElement | null>(null);
  const sentRef = useRef<Set<string>>(new Set());
  const once = (key: string, name: string, props: Record<string, string | number> = {}) => {
    if (sentRef.current.has(key)) return;
    sentRef.current.add(key);
    qqTrack(name, props);
  };
  // Funnel state for qq1_form_start, qq1_submit_click and qq1_abandon.
  const startedRef = useRef(false);
  const submittedRef = useRef(false);
  const lastFieldRef = useRef("none");
  const submitClicksRef = useRef(0);
  // Validation state. Errors keyed by control name; focusRequest moves focus
  // to the first invalid control after the messages render, so a screen
  // reader reads the message through aria-describedby.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [focusRequest, setFocusRequest] = useState<{ id: string; n: number } | null>(null);
  const validationFailsRef = useRef(0);
  const [emptyTap, setEmptyTap] = useState(false);
  // Touch screens get no focus on a failed Submit (see the layout effect),
  // so a screen reader hears the first problem from this live region
  // instead. The empty-form banner is already role="alert".
  const [liveMessage, setLiveMessage] = useState("");
  const submitBarRef = useRef<HTMLDivElement | null>(null);
  // noValidate only after hydration. Before that, the browser's own check
  // must still block a native submit of an empty form.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  // Cookie banner A/B test: step 1 viewed, once per browser (src/lib/banner-ab.ts).
  useEffect(() => countBannerStep("step1_view"), []);

  useLayoutEffect(() => {
    if (!focusRequest) return;
    const el = document.getElementById(focusRequest.id);
    if (!el) return;
    // One scroll so the first invalid row sits under the header and the
    // step bar; focus on desktop only (quick-quote-scroll.ts, #136).
    const row = document.getElementById(`${focusRequest.id}-row`) ?? el;
    scrollToInvalid(row, el, document.getElementById(EMPTY_FORM_ID), submitBarRef.current);
  }, [focusRequest]);

  function clearFieldError(target: EventTarget | null) {
    const el = target as Validatable | null;
    const name = el?.name;
    if (!el || !name || !(name in fieldErrors)) return;
    const consentUnchecked =
      el instanceof HTMLInputElement && name in CONSENT_FIELDS && !el.checked;
    if (consentUnchecked || !el.validity.valid) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  // Inline check when the visitor leaves a field they typed in (#69). Empty
  // fields are flagged only by Submit, not while the visitor tabs through.
  function checkOnBlur(target: EventTarget | null) {
    const el = target as Validatable | null;
    if (!el || !el.name || (el instanceof HTMLInputElement && el.type === "checkbox")) return;
    if (!String(el.value ?? "").trim()) return;
    if (el instanceof HTMLInputElement && el.name === "phone") {
      el.value = formatPhoneForDisplay(el.value);
    }
    const message = blurMessage(el);
    const name = el.name;
    setFieldErrors((prev) => {
      if ((prev[name] ?? null) === message) return prev;
      const next = { ...prev };
      if (message) next[name] = message;
      else delete next[name];
      return next;
    });
  }

  function errorProps(name: string) {
    return fieldErrors[name]
      ? { "aria-invalid": true as const, "aria-describedby": errorId(name) }
      : {};
  }

  function markStarted(target: EventTarget | null) {
    const field = trackedFieldName(target);
    if (field) lastFieldRef.current = field;
    if (startedRef.current) return;
    startedRef.current = true;
    startedAtRef.current = Date.now();
    once("start", "qq1_form_start", { ms: msSinceLoad() });
  }

  // qq1_abandon: the visitor started the form, then hid or left the page
  // without a send. zaraz.track has no beacon option, so this uses the same
  // helper; some events on page close can be lost.
  useEffect(() => {
    const onHide = () => {
      if (!startedRef.current || submittedRef.current) return;
      once("abandon", "qq1_abandon", { field: lastFieldRef.current, ms: msSinceLoad() });
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") onHide();
    };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  function onTurnstileToken(t: string) {
    setToken(t);
    hadTokenRef.current = true;
    turnstileIssueRef.current = null;
    challengeOpenRef.current = false;
    once("ready", "qq1_turnstile_ready", { ms: msSinceLoad() });
    // A token that arrives after the visitor saw a challenge. Once per page.
    if (sentRef.current.has("interactive")) {
      once("token_after_tap", "qq1_turnstile_token_after_tap", { ms: msSinceLoad() });
    }
    const waiter = tokenWaiterRef.current;
    if (waiter) {
      tokenWaiterRef.current = null;
      waiter(t);
    }
  }

  // Reset the widget and wait at most TOKEN_WAIT_MS for a new token.
  function waitForNewToken(): Promise<string | null> {
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        tokenWaiterRef.current = null;
        resolve(null);
      }, TOKEN_WAIT_MS);
      tokenWaiterRef.current = (t) => {
        window.clearTimeout(timer);
        resolve(t);
      };
      turnstileResetRef.current?.();
    });
  }

  // iOS suspends background tabs, and a token can expire there. Back on the
  // tab, get a new one quietly if it is old or gone. Not after an error: the
  // widget's own retries (at most 2, Turnstile.tsx) handle that, and a reset
  // on every return to the tab was a retry with no limit (23 Oct 2026).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible" || busyRef.current) return;
      if (turnstileIssueRef.current === "error") return;
      if (!tokenRef.current || Date.now() - tokenAtRef.current > TOKEN_REFRESH_MS) {
        turnstileResetRef.current?.();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Norwood class chosen on another page (session storage). When none was
  // picked upstream, the picker is shown inline above the form instead of
  // redirecting — this page is the single funnel entry point.
  //
  // The picker is in the server HTML. Most visitors arrive without a class,
  // and showing the picker only after the page hydrated pushed the name
  // fields down about 380 px on a phone: a layout shift of 0.119, over
  // Google's 0.1 limit (PageSpeed, 9 Oct 2026). A visitor who did choose a
  // class upstream has the picker removed before the first paint after
  // hydration.
  const [showPicker, setShowPicker] = useState(true);
  // A send that did not go through kept the fields in this tab (lead-retry.ts).
  // After a reload, put them back.
  useEffect(() => {
    restoreLeadDraft("qq1", formRef.current);
  }, []);
  useLayoutEffect(() => {
    const stored = readNorwood();
    if (stored) {
      setNorwood(stored);
      setShowPicker(false);
    }
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busyRef.current) return;
    const auto = autoResendRef.current;
    autoResendRef.current = false;
    // Validation runs first, as the browser check did before noValidate.
    // One qq1_validation_error per failed attempt: field is the first
    // invalid control, reason lists every invalid control (names only).
    const invalid = findInvalid(e.currentTarget);
    if (invalid.length > 0) {
      const empty = formIsEmpty(e.currentTarget);
      const errors: Record<string, string> = {};
      // Nothing filled in yet: one message above the first field, and the
      // empty contact fields marked red with their own message (18 Oct
      // 2026: First Name showed no error at all). The consent boxes stay
      // calm until the visitor has started. Once started, each invalid
      // control says why.
      for (const el of invalid) {
        if (empty && el.name in CONSENT_FIELDS) continue;
        errors[el.name] = invalidMessage(el);
      }
      setEmptyTap(empty);
      setLiveMessage(empty ? "" : invalidMessage(invalid[0]));
      setFieldErrors(errors);
      const first = invalid[0];
      setFocusRequest((prev) => ({ id: first.id, n: (prev?.n ?? 0) + 1 }));
      const shape = errorShape(invalid, empty);
      qqTrack("qq1_validation_error", {
        field: first.name || "unknown",
        reason: invalid
          .map((el) => el.name || "unknown")
          .join(",")
          .slice(0, 100),
        error_kind: shape.kind,
        invalid_fields: shape.invalid,
        attempt: ++validationFailsRef.current,
        ms: msSinceLoad(),
      });
      return;
    }
    setFieldErrors({});
    setEmptyTap(false);
    setLiveMessage("");
    once("valid", "qq1_validation_passed", {
      reason: validationFailsRef.current > 0 ? "after_error" : "first_try",
      attempt: validationFailsRef.current,
      ms: msSinceLoad(),
    });
    // End-to-end tests set this flag; the server still rejects the missing
    // token unless the request also carries the x-rh-test secret header.
    const testRun = typeof window !== "undefined" && window.sessionStorage.getItem("rh_test") === "1";
    const fd = new FormData(e.currentTarget);
    busyRef.current = true;
    submittedRef.current = true;
    setSubmitting(true);
    setError(null);
    const fresh = !!tokenRef.current && Date.now() - tokenAtRef.current < TOKEN_MAX_AGE_MS;
    let currentToken = fresh ? tokenRef.current : null;
    let bypass: "expired" | "error" | "missing" | "timeout" | undefined;
    if (tokenRef.current && !fresh) turnstileIssueRef.current ??= "expired";
    if (!currentToken && !testRun && turnstileIssueRef.current) {
      // A Turnstile error or an expired token with no new one yet (17 Oct
      // 2026): send now, without a token. The widget keeps retrying in the
      // background; a token that arrives later is used by the next send.
      bypass = turnstileIssueRef.current;
      setToken(null);
      qqTrack("qq1_turnstile_bypass", { reason: bypass, ms: msSinceLoad() });
    } else if (!currentToken && !testRun) {
      // No fresh token: get a new one, but wait at most TOKEN_WAIT_MS. The
      // message text is unchanged until Brian approves new wording.
      if (!auto) qqTrack("qq1_submit_blocked", { reason: "turnstile", ms: msSinceLoad() });
      setError("Verifying you're human. One moment, your quote will send automatically.");
      if (challengeOpenRef.current) {
        turnstileBoxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setToken(null);
      currentToken = await waitForNewToken();
      if (!currentToken) {
        bypass = turnstileIssueRef.current ?? (hadTokenRef.current ? "timeout" : "missing");
        qqTrack("qq1_turnstile_bypass", { reason: bypass, ms: msSinceLoad() });
      }
      setError(null);
    }
    // A token goes to the server once. A failed send asks for a new one.
    setToken(null);
    try {
      const firstName = String(fd.get("first_name") || "").trim();
      const lastName = String(fd.get("last_name") || "").trim();
      const emailValue = String(fd.get("email") || "").trim();
      const phoneValue = String(fd.get("phone") || "").trim();
      const zipValue = String(fd.get("zip") || "").trim();
      const res = await start({
        data: {
          turnstile_token: currentToken ?? (testRun ? "test-mode" : ""),
          turnstile_bypass: bypass,
          hp: String(fd.get("company_site") || ""),
          fill_ms: startedAtRef.current === null ? 0 : Date.now() - startedAtRef.current,
          first_name: firstName,
          last_name: lastName,
          email: emailValue,
          phone: phoneValue,
          zip: zipValue,
          norwood: norwood || undefined,
          heard_about: String(fd.get("heard") || "").trim() || undefined,
          consent_contact: true,
          consent_terms: true,
          consent_sensitive: true,
          page_path: window.location.pathname,
          referrer: document.referrer || undefined,
          tracking: withBannerVariant(getTrackingParams()),
        },
      });
      serverRetriesRef.current = 0;
      countBannerStep("step1_submit");
      clearLeadDraft("qq1");
      rememberW2lPreview(res);
      await trackLeadEventUnlessRepeat(res, {
        em: emailValue,
        ph: phoneValue,
        fn: firstName,
        ln: lastName,
        zp: zipValue,
      });
      rememberLeadId(res.lead_id);
      rememberPhone(phoneValue);
      rememberStep1Norwood(res.lead_id, norwood);
      clearNorwood();
      await navigate({ to: "/quick-quote-page-2", search: { qq: res.lead_id } });
    } catch (err) {
      submittedRef.current = false;
      // Keep what they typed in this tab until a send goes through.
      saveLeadDraft("qq1", formRef.current);
      const reason = (err instanceof Error ? err.message : "unknown").slice(0, 60);
      if (!testRun && serverRetriesRef.current < LEAD_RETRY_DELAYS_MS.length) {
        // The server has used this token. Never send it again. Wait a
        // little (14 Oct 2026: 2 s, then 5 s, so a short D1 or Sheet outage
        // can pass), then send the form again: onSubmit gets a new token (or
        // bypasses Turnstile after TOKEN_WAIT_MS). No second tap.
        const attempt = ++serverRetriesRef.current;
        qqTrack("qq1_submit_error", { reason: "retry_new_token", attempt, field: reason });
        setToken(null);
        setError("Still sending, one moment…");
        await sleep(LEAD_RETRY_DELAYS_MS[attempt - 1] ?? 2_000);
        busyRef.current = false;
        autoResendRef.current = true;
        window.setTimeout(() => formRef.current?.requestSubmit(), 0);
      } else {
        setError(finalLeadError(err));
        qqTrack("qq1_submit_error", { reason });
        serverRetriesRef.current = 0;
        busyRef.current = false;
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-6 lg:px-8 md:py-16">
        <div className="grid gap-8 lg:grid-cols-2 lg:gap-16">
          {/* Left — brand column */}
          <div>
            <svg
              aria-hidden="true"
              viewBox="0 0 48 16"
              className="h-4 w-12 text-primary"
              fill="none"
            >
              <path
                d="M1 12C10 3 20 1 30 4c7 2 12 6 17 9"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            <p className="mt-4 hidden text-sm text-muted-foreground lg:block">RESTORE® Hair</p>
            <h1 className="mt-3 text-4xl font-light leading-tight tracking-tight text-foreground md:text-5xl lg:mt-6">
              Get a Quick <span className="font-extrabold">Quote!</span>
            </h1>
            <p className="mt-3 text-base text-foreground lg:mt-6">
              Discover how affordable it is to get your hair back.
            </p>

            {/* Testimonial: beside the form on desktop. On phones it pushed the
                first field below the fold, so it is shown under the form. */}
            <div className="mt-10 hidden items-center gap-6 lg:flex">
              <img
                // 208 px copy for this 104 px circle. The full portrait is
                // 41 KB; this copy is a few KB.
                src="/img/doug-portrait-208.webp"
                alt="Doug M. before and after his RESTORE hair restoration procedure"
                width={104}
                height={104}
                loading="lazy"
                className="h-26 w-26 shrink-0 rounded-full object-cover shadow-md"
                style={{ height: "104px", width: "104px" }}
              />
              <div>
                <div className="flex gap-1 text-lg leading-none text-[#f5b301]" aria-label="5 out of 5 stars">
                  <span aria-hidden="true">★★★★★</span>
                </div>
                <p className="mt-3 max-w-xs text-base leading-relaxed text-foreground">
                  &ldquo;This was by far the best thing I&apos;ve ever done for myself.&rdquo; - Doug M
                </p>
              </div>
            </div>
          </div>

          {/* Right — form column */}
          <div>
            <QuickQuoteStepLabel current={1} label="Your details" />

            <h2 className="mt-6 text-2xl font-light text-foreground md:text-3xl">
              Tell Us About Yourself
            </h2>

            <form
              ref={formRef}
              onSubmit={onSubmit}
              onFocus={(e) => {
                const field = (e.target as { name?: string }).name || "unknown";
                once("first", "qq1_first_field", { field, ms: msSinceLoad() });
                markStarted(e.target);
              }}
              // Before hydration a native submit must never put the fields
              // in a URL: POST keeps them out of the query string, and the
              // button stays disabled until hydration, which also blocks the
              // Enter key (implicit submit needs an enabled default button).
              method="post"
              noValidate={hydrated}
              onInput={(e) => {
                markStarted(e.target);
                clearFieldError(e.target);
                setEmptyTap(false);
              }}
              onBlur={(e) => {
                checkOnBlur(e.target);
                const el = e.target as unknown as HTMLInputElement | HTMLSelectElement;
                const field = trackedFieldName(el);
                if (!field || field === "other" || field.startsWith("consent_")) return;
                if (!String(el.value ?? "").trim()) return;
                once(`done:${field}`, "qq1_field_done", { field, ms: msSinceLoad() });
              }}
              onChange={(e) => {
                clearFieldError(e.target);
                setEmptyTap(false);
                const el = e.target as unknown as HTMLInputElement;
                if (el.type !== "checkbox" || !el.checked) return;
                const box = CONSENT_FIELDS[el.name];
                if (!box) return;
                once(`consent:${box}`, "qq1_consent_checked", { field: box, ms: msSinceLoad() });
              }}
              className="mt-6 space-y-4"
            >
              {showPicker && (
                <fieldset className="scroll-mt-24">
                  <legend className="text-lg font-light text-foreground md:text-xl">
                    Which image most closely{" "}
                    <span className="font-extrabold">resembles your hair loss?</span>
                    <span className="text-sm font-normal text-muted-foreground"> (optional)</span>
                  </legend>
                  <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                    {norwoodClasses.map((c) => {
                      const selected = norwood === c.label;
                      return (
                        <button
                          type="button"
                          key={c.label}
                          aria-pressed={selected}
                          onClick={() => {
                            setNorwood(c.label);
                            setError(null);
                            lastFieldRef.current = "norwood";
                            markStarted(null);
                            once("norwood", "qq1_norwood_selected", {
                              field: c.label.toLowerCase().replace(/\s+/g, "_"),
                              ms: msSinceLoad(),
                            });
                          }}
                          className={`group relative flex flex-col items-center border-2 bg-background p-3 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${
                            selected
                              ? "border-primary shadow-lg"
                              : "border-border hover:border-primary hover:bg-primary/5"
                          }`}
                        >
                          <span
                            className={`absolute -top-3 left-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border-2 transition ${
                              selected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-muted bg-background text-muted-foreground"
                            }`}
                          >
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </span>
                          <img
                            src={c.img}
                            alt={`Norwood ${c.label} hair loss illustration`}
                            width={204}
                            height={130}
                            loading="lazy"
                            className="mt-3 h-20 w-auto origin-center object-contain transition-transform duration-200 ease-out [transform:scale(1)] group-hover:[transform:scale(1.12)]"
                          />
                          <span className="mt-2 text-sm font-bold text-foreground transition-colors group-hover:text-primary">
                            {c.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              )}
              <p className="sr-only" aria-live="polite">
                {liveMessage}
              </p>
              {emptyTap && (
                <p
                  id={EMPTY_FORM_ID}
                  role="alert"
                  className="border-l-4 border-destructive bg-destructive/5 px-3 py-2 text-sm font-medium text-destructive"
                >
                  {EMPTY_FORM_MESSAGE}
                </p>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="first_name" label="First Name *" error={fieldErrors.first_name}>
                  <input
                    className={controlCls}
                    id="first_name"
                    name="first_name"
                    type="text"
                    {...errorProps("first_name")}
                    {...(emptyTap
                      ? {
                          "aria-describedby": fieldErrors.first_name
                            ? `${EMPTY_FORM_ID} ${errorId("first_name")}`
                            : EMPTY_FORM_ID,
                        }
                      : {})}
                    required
                    maxLength={80}
                    autoComplete="given-name"
                    autoCapitalize="words"
                    enterKeyHint="next"
                  />
                </Field>
                <Field id="last_name" label="Last Name *" error={fieldErrors.last_name}>
                  <input
                    className={controlCls}
                    id="last_name"
                    name="last_name"
                    type="text"
                    {...errorProps("last_name")}
                    required
                    maxLength={80}
                    autoComplete="family-name"
                    autoCapitalize="words"
                    enterKeyHint="next"
                  />
                </Field>
              </div>
              <Field id="phone" label="Phone *" error={fieldErrors.phone}>
                <input
                  className={controlCls}
                  id="phone"
                  name="phone"
                  {...errorProps("phone")}
                  type="tel"
                  inputMode="tel"
                  required
                  maxLength={32}
                  autoComplete="tel"
                  enterKeyHint="next"
                  placeholder="(555) 555-5555"
                />
              </Field>
              <Field id="email" label="Email *" error={fieldErrors.email}>
                <input
                  className={controlCls}
                  id="email"
                  name="email"
                  type="email"
                  inputMode="email"
                  {...errorProps("email")}
                  required
                  maxLength={255}
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="next"
                />
              </Field>
              <Field id="zip" label="Zip Code *" error={fieldErrors.zip}>
                <input
                  className={controlCls}
                  id="zip"
                  name="zip"
                  type="text"
                  inputMode="numeric"
                  {...errorProps("zip")}
                  required
                  maxLength={20}
                  autoComplete="postal-code"
                  enterKeyHint="next"
                />
              </Field>
              <Field id="heard" label="How did you hear about RESTORE?">
                <select className={controlCls} id="heard" name="heard" defaultValue="">
                  <option value="">Select one</option>
                  {QQ_HEARD_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </Field>

              {/* Norwood class carried over from the picker on another page */}
              <input type="hidden" name="norwood" value={norwood} />

              <div className="space-y-3 pt-2 text-xs leading-relaxed text-foreground">
                <Consent id="consent_contact" error={fieldErrors.consent_contact}>
                  By checking this box and clicking the Get a Quick Quote button below, I consent
                  to receive automated marketing, advertising, transactional, or informational
                  phone calls, text messages, or emails, potentially involving use of an
                  autodialer or an artificial or prerecorded voice, from or on behalf of Restore
                  Holdings LLC (“Restore”) at the phone number or email address I provided above.
                  I understand my consent is not a condition of purchase. Reply “STOP” to opt-out
                  of text messages.
                </Consent>
                <Consent id="consent_terms" error={fieldErrors.consent_terms}>
                  By checking this box and clicking the Get a Quick Quote button below, I also
                  agree to abide by Restore’s{" "}
                  <a className="text-primary underline" href="/terms-of-use/">Terms of Use</a> and{" "}
                  <a className="text-primary underline" href="/privacy-policy/">Privacy Policy</a>,
                  including submitting all disputes to binding individual arbitration.
                </Consent>
                <Consent id="consent_sensitive" error={fieldErrors.consent_sensitive}>
                  <span className="uppercase">
                    By checking this box and clicking the Get a Quick Quote button below, I consent
                    to Restore’s collection and processing of your “sensitive personal information”
                    for the purposes described in our{" "}
                    <a className="text-primary underline" href="/privacy-policy/">Privacy Policy</a>.
                  </span>
                </Consent>
                <p className="uppercase">
                  California Residents: See our{" "}
                  <a className="text-primary underline" href="/privacy-policy/#ccpa-notice">
                    CCPA notice at collection
                  </a>
                </p>
              </div>

              {/* Honeypot (8 Oct 2026): off screen, hidden from screen readers,
                  out of the tab order. People leave it empty; a value marks
                  a bot when Turnstile did not verify. */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
              >
                <label htmlFor="qq_company_site">Leave this field empty</label>
                <input id="qq_company_site" name="company_site" type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
              </div>

              <div className="pt-2" ref={turnstileBoxRef}>
                <Turnstile
                  lazy
                  action="quick-quote"
                  appearance="interaction-only"
                  backoff
                  onToken={onTurnstileToken}
                  resetRef={turnstileResetRef}
                  onBeforeInteractive={() => {
                    challengeOpenRef.current = true;
                    once("interactive", "qq1_turnstile_interactive", { ms: msSinceLoad() });
                  }}
                  onAfterInteractive={() => {
                    challengeOpenRef.current = false;
                    once("interactive_done", "qq1_turnstile_interactive_done", {
                      ms: msSinceLoad(),
                    });
                  }}
                  onTimeout={() => {
                    // Cloudflare shows a new challenge by itself about every
                    // 2 minutes; only the first timeout on a page is sent.
                    reportTurnstileProblem("timeout");
                  }}
                  onUnsupported={() => reportTurnstileProblem("unsupported")}
                  onExpired={() => {
                    setToken(null);
                    turnstileIssueRef.current = "expired";
                    // Get a new token quietly, with no message.
                    turnstileResetRef.current?.();
                    // Turnstile refreshes an expired token by itself every
                    // ~5 minutes, so an idle tab fires this many times.
                    // Count it once per page.
                    once("expired", "qq1_turnstile_expired", { ms: msSinceLoad() });
                  }}
                  onError={(code) => {
                    setToken(null);
                    turnstileIssueRef.current = "error";
                    challengeOpenRef.current = false;
                    reportTurnstileProblem(code);
                    // No reset here: the widget retries in the background with
                    // growing waits (backoff), and Submit sends without a
                    // token from now on. The visitor sees no message.
                  }}
                />
              </div>

              {/* On phones the button stays pinned to the bottom of the screen while
                  the form is in view, so it never has to be hunted for. The
                  site's mobile quote bar does not render on /quick-quote pages. */}
              <div
                ref={submitBarRef}
                className={QQ_STICKY_BAR_CLS}
              >
                {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting || !hydrated}
                  onClick={() =>
                    qqTrack("qq1_submit_click", {
                      attempt: ++submitClicksRef.current,
                      reason: "",
                      ms: msSinceLoad(),
                    })
                  }
                  className="w-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Get A Quick Quote"}
                </button>
                <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
                  <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  Free and no obligation. We&apos;ll call you with your price.
                </p>
              </div>
            </form>

            {/* Phones only: the testimonial from the brand column, under the form. */}
            <div className="mt-8 flex items-center gap-4 lg:hidden">
              <img
                src="/img/doug-portrait-208.webp"
                alt="Doug M. before and after his RESTORE hair restoration procedure"
                width={72}
                height={72}
                loading="lazy"
                className="shrink-0 rounded-full object-cover shadow-md"
                style={{ height: "72px", width: "72px" }}
              />
              <div>
                <div className="text-base leading-none text-[#f5b301]" aria-label="5 out of 5 stars">
                  <span aria-hidden="true">★★★★★</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground">
                  &ldquo;This was by far the best thing I&apos;ve ever done for myself.&rdquo; - Doug M
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
